import { useMutation } from "@tanstack/react-query";

import { deleteDataProvider } from "api";
import { QUERY_KEYS } from "consts";

/**
 * Custom React hook for deleting a data provider and handling related UI updates.
 *
 * This hook wraps a mutation using `react-query`'s `useMutation` to perform
 * an asynchronous deletion of a data provider. It provides success and error
 * notifications and invalidates cached site queries to ensure the UI stays in sync.
 *
 * @function useDeleteDataProvider
 * @param {Object} notify - Object containing notification methods.
 * @param {Function} notify.success - Function to display success notifications.
 * @param {Function} notify.error - Function to display error notifications.
 * @param {Object} queryClient - React Query client instance used for invalidating queries.
 * @returns {import('@tanstack/react-query').UseMutationResult} A React Query mutation object.
 *
 * @example
 * const notify = {
 *   success: (msg) => toast.success(msg),
 *   error: (msg) => toast.error(msg),
 * };
 *
 * const queryClient = useQueryClient();
 *
 * const deleteDataProvider = useDeleteDataProvider(notify, queryClient);
 *
 * deleteDataProvider.mutate({ siteId: 1, userId: 42 });
 */
export const useDeleteDataProvider = (notify: any, queryClient: any) => {
  const mutationFn = ({ siteId, userId }: { siteId: number; userId: number }) =>
    deleteDataProvider(siteId, userId);

  return useMutation(mutationFn, {
    onSuccess: () => {
      notify.success('Data provider successfully added!');
      queryClient.invalidateQueries([QUERY_KEYS.SITES]);
    },
    onError: () => {
      notify.error('Some error has occurred while adding the data provider!');
    },
  });
};
